import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { Location } from "@/models/location.model"
import type { ILocation } from "@/interfaces/api/location.interface"
import { LocationRepository } from "@/repositories/location-repository"
import { useLocationStore } from "@/store/locations.store"
import { storeToRefs } from "pinia"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { ODataQueryBuilder } from "@/builder/odata-query-builder.ts"

export class LocationController extends GenericModelController<
  Location,
  ILocation,
  LocationRepository
> {
  constructor() {
    const locationStore = useLocationStore()
    const { tenantBaseLocations } = storeToRefs(locationStore)
    const storeFunctionsConfig = {
      onCreate: (location: Location) => {
        if (
          location.isTenantBase &&
          !tenantBaseLocations.value.some(
            (tenantBaseLocation) => tenantBaseLocation.id === location.id
          )
        ) {
          tenantBaseLocations.value.push(location)
        }
      }
    }

    super(Location, storeFunctionsConfig, new LocationRepository())

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      Location,
      null,
      (location: Location) => {
        if (location.isTenantBase) {
          tenantBaseLocations.value.push(location)
        }
      },
      (location: Location) => {
        if (location.isTenantBase) {
          const index = tenantBaseLocations.value.findIndex((l) => l.id === location.id)
          if (index !== -1) {
            tenantBaseLocations.value.splice(index, 1)
          }
        }
      },
      false // Otherwise locations will not be synced from app to planning for the same session
    )
  }

  public async getTenantBaseLocationsList(): Promise<Location[]> {
    const qb = new ODataQueryBuilder()
    qb.eq("is_tenant_base", true)

    return this.getList({ query: qb })
  }
}
